"use client";

import { useState } from "react";
import { usePathname, Link } from "@/src/navigation";
import Image from "next/image";
import Container from "@/src/components/ui/Container";
import HumburgerMenu from "@/src/components/ui/HumburgerMenu";
import NavLink from "@/src/components/navbar/NavLink";
import { useTranslations, useLocale } from "next-intl";
import RegisterNowButton from "@/src/components/ui/RegisterNowButton";
import FullLogo from "@/public/icons/full-logo.svg";

const Navbar = () => {
  const tLinks = useTranslations("Navbar.links");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const pathname = usePathname();
  const locale = useLocale();
  const isLtr = locale === "en";

  const links = [
    { id: 1, key: "home", route: "/" },
    { id: 2, key: "about", route: "/about" },
    { id: 3, key: "services", route: "/services" },
    { id: 4, key: "universities", route: "/universities" },
    { id: 5, key: "programs", route: "/programs" },
    { id: 6, key: "contact", route: "/contact" },
    { id: 7, key: "blog", route: "/bloger" },
  ];

  function handleLinkClick() {
    setIsMenuOpen(false);
  }

  return (
    <nav
      className={`sticky top-0 z-50 w-full h-fit md:h-20 bg-surface nav-shadow transition-shadow`}
      dir="ltr"
    >
      <Container className="flex justify-between items-center w-full">
        {/* Right side: CTA Button (desktop) and Hamburger (mobile) */}
        <div className="flex items-center gap-4">
          {/* CTA Button - Hidden on mobile, shown on desktop */}
          <RegisterNowButton className="hidden md:block" wide />
        </div>

        {/* Links - Center (hidden on mobile) */}
        <ul
          dir={isLtr ? "ltr" : "rtl"}
          className={`hidden lg:flex items-center shrink-0 lg:px-6 rtl:gap-6 ltr:gap-3 ltr:xl:gap-6`}
        >
          {links.map((link) => (
            <NavLink
              link={{ ...link, text: tLinks(link.key) }}
              key={link.id}
              handleLinkClick={handleLinkClick}
              setIsMenuOpen={setIsMenuOpen}
              isActive={pathname === link.route}
            />
          ))}
        </ul>

        <div
          className="flex items-center py-4 gap-4 w-full md:w-fit justify-between flex-row-reverse md:flex-row md:justify-start"
          dir="rtl"
        >
          {/* Hamburger Menu Button */}
          <HumburgerMenu
            isMenuOpen={isMenuOpen}
            setIsMenuOpen={setIsMenuOpen}
          />
          {/* Logo */}
          <Link href="/">
            <Image
              src={FullLogo}
              alt="The Study Solution - Educational Consultancy Official Logo"
              className="w-auto h-9 md:h-12 shrink-0"
              onContextMenu={(e) => e.preventDefault()}
            />
          </Link>
        </div>
      </Container>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-surface shadow-bottom lg:hidden">
          <ul className="flex flex-col pb-4 md:pb-0">
            {links.map((link) => (
              <NavLink
                link={{ ...link, text: tLinks(link.key) }}
                setIsMenuOpen={setIsMenuOpen}
                key={link.id}
                handleLinkClick={handleLinkClick}
                isActive={pathname === link.route}
              />
            ))}
            {/* CTA Button in mobile menu */}
            <li className="px-5 pt-2 md:hidden">
              <RegisterNowButton className="w-full" wide />
            </li>
          </ul>
        </div>
      )}

      {/* Sticky Tabs Portal Target */}
      <div
        dir={isLtr ? "ltr" : "rtl"}
        id="navbar-sticky-tabs"
        className="w-full bg-surface"
      />
    </nav>
  );
};

export default Navbar;
